/**
 * Why a Delete would be refused — computed on the client so the operator gets a REASON instead of a
 * dead button.
 *
 * This is NOT the gate. Every one of these rules is enforced server-side in admin/service.delete_user
 * (R5 disable-first, R2 self, R4 only-an-Owner-deletes-an-Owner, R1 last-active-Owner) inside a
 * FOR UPDATE lock, and the server's 4xx is surfaced verbatim if it ever disagrees with us — which it
 * can, legitimately: this list is a snapshot, and someone else can re-enable or demote a user between
 * the render and the click. A grayed-out button is a convenience; the lock is the guard.
 *
 * The ORDER mirrors the server's check order exactly, so the sentence we show pre-flight is the same
 * refusal the server would give post-click. If they diverged, we'd be explaining a rule that isn't the
 * one that fires.
 */

import type { AdminUser, Me } from "@/api/types";

export type DeleteRefusal =
  /** R2 — you cannot delete your own account (mirror of the self-lockout rule on Disable). */
  | "self"
  /** R4 — an Admin holds user:manage but must not be able to destroy an Owner. */
  | "owner_needs_owner"
  /** R5 — delete only removes an ALREADY-DISABLED account. Two deliberate steps, not one. */
  | "still_active"
  /** R1 — refuses to destroy an Owner row when the portal has ZERO active Owners left. In that state
   *  the disabled Owner row IS the recovery path, and deleting it is the last irreversible step into a
   *  portal nobody can administer. */
  | "last_owner";

/**
 * The refusal that would fire for `target`, or null if the delete is allowed.
 *
 * `all` is the full list from GET /admin/users — needed only for the last-Owner count. We count ACTIVE
 * Owners other than the target: that is precisely what the server's _assert_not_last_owner(exclude_id)
 * does. (Because R5 already forces the target to be inactive, the target is never in the active set
 * anyway — the exclusion is belt-and-braces, and it keeps this function honest if R5 ever moves.)
 */
export function deleteRefusal(
  target: AdminUser,
  actor: Me,
  all: AdminUser[],
): DeleteRefusal | null {
  if (target.user_id !== null && target.user_id === actor.id) return "self";
  if (target.role === "owner" && actor.role !== "owner") return "owner_needs_owner";
  if (target.is_active) return "still_active";
  if (target.role === "owner" && countOtherActiveOwners(all, target) === 0) return "last_owner";
  return null;
}

function countOtherActiveOwners(all: AdminUser[], target: AdminUser): number {
  return all.filter(
    (u) =>
      u.provisioned &&
      u.role === "owner" &&
      u.is_active === true &&
      u.user_id !== null &&
      u.user_id !== target.user_id,
  ).length;
}
